import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Phone, Calendar, Check, X, HelpCircle, Download,
  Loader2, Save, UtensilsCrossed, ShoppingBag, MailOpen, MousePointerClick,
  MapPin, Building2, MailCheck, MailX, CircleSlash, Undo2,
} from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/layout/EmptyState';
import { formatDateLong, formatTime12, zoneAbbr } from '../lib/time';
import { downloadCsv, slugifyName } from '../lib/csv';

const RSVP_BADGE = {
  yes: { label: 'Going', cls: 'text-primary', icon: Check },
  maybe: { label: 'Maybe', cls: 'text-accent', icon: HelpCircle },
  no: { label: "Can't go", cls: 'text-muted-foreground', icon: X },
};

const StatCard = ({ value, label, accent }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={`profile-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className={`font-display text-2xl font-semibold ${accent || 'text-foreground'}`}>{value}</div>
    <div className="text-sm text-muted-foreground">{label}</div>
  </div>
);

export default function ContactProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [contact, setContact] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const { data } = await api.get(`/contacts/${id}/history`);
      setContact(data.contact);
      setStats(data.stats);
      setHistory(data.history || []);
      setNotes(data.contact?.notes || '');
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else toast.error('Could not load guest profile');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/contacts/${id}`, { notes });
      setContact((c) => ({ ...c, notes }));
      toast.success('Notes saved');
    } catch (e) {
      toast.error('Could not save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const [subBusy, setSubBusy] = useState(false);
  const toggleSubscription = async () => {
    const isUnsub = !!contact.unsubscribed;
    setSubBusy(true);
    try {
      await api.post(isUnsub ? '/contacts/bulk-resubscribe' : '/contacts/bulk-unsubscribe', { ids: [id] });
      setContact((c) => ({ ...c, unsubscribed: !isUnsub, status: 'active' }));
      toast.dismiss();
      toast.success(isUnsub ? 'Contact restored — they can be emailed again' : 'Contact unsubscribed from emails');
    } catch (e) {
      toast.error('Could not update subscription');
    } finally { setSubBusy(false); }
  };

  const exportHistory = () => {
    const headers = [
      { key: 'title', label: 'Event' }, { key: 'date', label: 'Date' },
      { key: 'invited', label: 'Invited' }, { key: 'delivered', label: 'Delivered' },
      { key: 'opened', label: 'Opened' }, { key: 'clicked', label: 'Clicked' },
      { key: 'bounced', label: 'Bounced' }, { key: 'rsvp', label: 'RSVP' },
      { key: 'guests', label: 'Party size' }, { key: 'attended', label: 'Attended' },
      { key: 'brought', label: 'Brought' }, { key: 'dietary', label: 'Dietary' },
    ];
    const yn = (b) => (b ? 'Yes' : 'No');
    const rows = history.map((h) => ({
      title: h.title, date: h.date,
      invited: yn(h.invited), delivered: yn(h.delivered),
      opened: yn(h.opened), clicked: yn(h.clicked), bounced: yn(h.bounced),
      rsvp: h.rsvp_status || (h.no_response ? 'No response' : '—'),
      guests: h.guests || 0,
      attended: yn(h.attended),
      brought: (h.brought || []).map((b) => `${b.label}: ${b.value}`).join('; '),
      dietary: (h.dietary || []).map((d) => d.value).join('; '),
    }));
    const name = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email;
    downloadCsv(`${slugifyName(name)}-history`, headers, rows);
    toast.success('Exported guest history');
  };

  if (loading) {
    return (
      <div data-testid="contact-profile-page">
        <Skeleton className="h-9 w-40 mb-6 rounded-lg" />
        <Skeleton className="h-32 w-full mb-4 rounded-2xl" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
    );
  }

  if (notFound || !contact) {
    return (
      <div data-testid="contact-profile-page">
        <Button variant="ghost" onClick={() => navigate('/app/contacts')} className="gap-2 mb-4" data-testid="profile-back-button">
          <ArrowLeft className="h-4 w-4" /> Back to contacts
        </Button>
        <div className="rounded-2xl border border-border bg-card">
          <EmptyState icon={X} title="Guest not found" description="This contact may have been deleted or merged." />
        </div>
      </div>
    );
  }

  const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || '—';
  const initials = (`${contact.first_name?.[0] || ''}${contact.last_name?.[0] || ''}`.trim()
    || contact.email?.[0] || '?').toUpperCase();

  return (
    <div data-testid="contact-profile-page">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <Button variant="ghost" onClick={() => navigate('/app/contacts')} className="gap-2" data-testid="profile-back-button">
          <ArrowLeft className="h-4 w-4" /> Back to contacts
        </Button>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={contact.unsubscribed ? 'secondary' : 'ghost'}
            onClick={toggleSubscription}
            disabled={subBusy}
            className={`gap-2 ${contact.unsubscribed ? '' : 'text-accent'}`}
            data-testid="profile-subscription-toggle"
          >
            {subBusy ? <Loader2 className="h-4 w-4 animate-spin" />
              : contact.unsubscribed ? <Undo2 className="h-4 w-4" /> : <MailX className="h-4 w-4" />}
            {contact.unsubscribed ? 'Restore to contacts' : 'Unsubscribe'}
          </Button>
          {history.length > 0 && (
            <Button variant="secondary" onClick={exportHistory} className="gap-2" data-testid="profile-export-button">
              <Download className="h-4 w-4" /> Export history
            </Button>
          )}
        </div>
      </div>

      {/* Identity card */}
      <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 mb-4 shadow-[0_1px_0_rgba(17,24,39,0.04),0_10px_30px_rgba(17,24,39,0.06)]">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center font-display text-2xl font-semibold text-primary flex-shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-foreground" data-testid="profile-name">{fullName}</h1>
              {contact.unsubscribed && <Badge variant="secondary" className="text-accent gap-1"><MailX className="h-3 w-3" /> Unsubscribed</Badge>}
              {contact.status === 'inactive'
                ? <Badge variant="secondary" className="text-muted-foreground">Inactive</Badge>
                : <Badge variant="secondary" className="text-primary">Active</Badge>}
            </div>
            <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-x-5 gap-y-1 text-sm text-muted-foreground">
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="inline-flex items-center gap-1.5 hover:text-primary transition-colors" data-testid="profile-email">
                  <Mail className="h-3.5 w-3.5" /> {contact.email}
                </a>
              )}
              {contact.phone && (
                <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {contact.phone}</span>
              )}
            </div>
            {(contact.company || contact.address || contact.dietary) && (
              <div className="mt-2 flex flex-col gap-1 text-sm text-muted-foreground" data-testid="profile-details">
                {contact.company && (
                  <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 flex-shrink-0" /> {contact.company}</span>
                )}
                {contact.address && (
                  <span className="inline-flex items-start gap-1.5"><MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> {contact.address}</span>
                )}
                {contact.dietary && (
                  <span className="inline-flex items-start gap-1.5"><UtensilsCrossed className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> <span><span className="text-foreground/70">Dietary/allergies: </span>{contact.dietary}</span></span>
                )}
              </div>
            )}
            {(contact.tags || []).length > 0 && (
              <div className="mt-3 flex gap-1.5 flex-wrap">
                {contact.tags.map((t) => <Badge key={t} variant="secondary" className="font-normal">{t}</Badge>)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatCard value={stats?.events_invited ?? 0} label="Invited" />
        <StatCard value={stats?.events_responded ?? 0} label="Responded" />
        <StatCard value={stats?.events_going ?? 0} label="Going" accent="text-accent" />
        <StatCard value={stats?.events_attended ?? 0} label="Attended" accent="text-primary" />
      </div>

      {/* Email engagement */}
      <div className="rounded-2xl border border-border bg-card p-5 mb-4" data-testid="profile-email-engagement">
        <h2 className="font-display text-lg font-semibold mb-3">Email engagement</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="text-center rounded-xl bg-secondary/40 p-3"><div className="font-display text-2xl font-semibold text-primary">{stats?.emails_delivered ?? 0}</div><div className="text-xs text-muted-foreground mt-0.5">Delivered</div></div>
          <div className="text-center rounded-xl bg-secondary/40 p-3"><div className="font-display text-2xl font-semibold text-accent">{stats?.emails_opened ?? 0}</div><div className="text-xs text-muted-foreground mt-0.5">Opened</div></div>
          <div className="text-center rounded-xl bg-secondary/40 p-3"><div className="font-display text-2xl font-semibold text-accent">{stats?.emails_clicked ?? 0}</div><div className="text-xs text-muted-foreground mt-0.5">Clicked</div></div>
          <div className="text-center rounded-xl bg-secondary/40 p-3"><div className="font-display text-2xl font-semibold text-destructive">{stats?.emails_bounced ?? 0}</div><div className="text-xs text-muted-foreground mt-0.5">Bounced</div></div>
        </div>
      </div>

      {/* Host notes */}
      <div className="rounded-2xl border border-border bg-card p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-semibold">Host notes</h2>
          <Button size="sm" onClick={saveNotes} disabled={savingNotes || notes === (contact.notes || '')} className="gap-1.5" data-testid="profile-save-notes-button">
            {savingNotes ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
          </Button>
        </div>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Private notes about this guest (preferences, history, reminders). Only you can see this."
          className="min-h-[100px] resize-y"
          data-testid="profile-notes-input"
        />
      </div>

      {/* Event history */}
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="profile-history">
        <h2 className="font-display text-lg font-semibold mb-4">Event history</h2>
        {history.length === 0 ? (
          <EmptyState icon={Calendar} title="No event history yet"
            description="Once this guest is invited to or RSVPs for an event, it will show here." />
        ) : (
          <div className="space-y-3">
            {history.map((h) => {
              const badge = RSVP_BADGE[h.rsvp_status];
              const RsvpIcon = badge?.icon;
              return (
                <div key={h.event_id} className="rounded-xl border border-border p-4" data-testid="profile-history-row">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <button
                        onClick={() => navigate(`/app/events/${h.event_id}`)}
                        className="font-medium text-foreground hover:text-primary transition-colors text-left"
                        data-testid="profile-history-event-link"
                      >
                        {h.title || 'Untitled event'}
                      </button>
                      <div className="mt-0.5 text-sm text-muted-foreground flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" />
                        {h.date ? formatDateLong(h.date) : '—'}
                        {h.time && <span>· {formatTime12(h.time)} {zoneAbbr(h.timezone)}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {h.invited && <Badge variant="secondary" className="font-normal">Invited</Badge>}
                      {h.delivered && <Badge variant="secondary" className="font-normal text-primary gap-1"><MailCheck className="h-3 w-3" /> Delivered</Badge>}
                      {h.opened && <Badge variant="secondary" className="font-normal text-accent gap-1"><MailOpen className="h-3 w-3" /> Opened</Badge>}
                      {h.clicked && <Badge variant="secondary" className="font-normal text-accent gap-1"><MousePointerClick className="h-3 w-3" /> Clicked</Badge>}
                      {h.bounced && <Badge variant="secondary" className="font-normal text-destructive gap-1"><MailX className="h-3 w-3" /> Bounced</Badge>}
                      {badge && (
                        <Badge variant="secondary" className={`font-normal gap-1 ${badge.cls}`}>
                          {RsvpIcon && <RsvpIcon className="h-3 w-3" />} {badge.label}
                          {Number(h.guests) > 1 && ` · party of ${h.guests}`}
                        </Badge>
                      )}
                      {h.no_response && <Badge variant="secondary" className="font-normal text-muted-foreground gap-1"><CircleSlash className="h-3 w-3" /> No response</Badge>}
                      {h.attended && <Badge variant="secondary" className="font-normal text-primary gap-1"><Check className="h-3 w-3" /> Checked in</Badge>}
                    </div>
                  </div>

                  {(h.brought?.length > 0 || h.dietary?.length > 0 || h.message) && (
                    <div className="mt-3 pt-3 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      {h.brought?.length > 0 && (
                        <div className="inline-flex items-start gap-1.5">
                          <ShoppingBag className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                          <span><span className="text-muted-foreground">Brought: </span>{h.brought.map((b) => b.value).join(', ')}</span>
                        </div>
                      )}
                      {h.dietary?.length > 0 && (
                        <div className="inline-flex items-start gap-1.5">
                          <UtensilsCrossed className="h-3.5 w-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                          <span><span className="text-muted-foreground">Dietary: </span>{h.dietary.map((d) => d.value).join(', ')}</span>
                        </div>
                      )}
                      {h.message && (
                        <div className="sm:col-span-2 text-muted-foreground italic">&ldquo;{h.message}&rdquo;</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
